import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { adminActions, type CmtsAdminPermissionEntity, type CmtsAdminRoleEntity, type CmtsAdminUserEntity } from "../store/adminSlice.js";
import { useCmtsDispatch } from "../store/hooks.js";
import type { EntityRecord, EntitySchemaField, PageQuery, ValidationFieldError } from "../types/api.types.js";
import type { CmtsAdminBlockedState, CmtsAdminIdentityPageConfig, CmtsAdminIdentityPageState } from "../types/adminIdentity.types.js";
import type { CmtsFormField } from "../components/forms/index.js";
import { normalizeUserSafeApiError, redactTechnicalText } from "../core/api/apiErrorNormalizer.js";

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function readId(row: EntityRecord): string {
  return readString(row.id) ?? readString(row.userId) ?? readString(row.roleId) ?? readString(row.permissionId) ?? readString(row.tenantId) ?? readString(row.orgUnitId) ?? readString(row.code) ?? "";
}

function readLabel(row: EntityRecord): string {
  return readString(row.displayName) ?? readString(row.label) ?? readString(row.name) ?? readString(row.title) ?? readString(row.loginId) ?? readString(row.email) ?? readString(row.code) ?? readId(row);
}

function isActive(row: EntityRecord): boolean {
  if (typeof row.active === "boolean") return row.active;
  if (typeof row.isActive === "boolean") return row.isActive;
  return String(row.status ?? "active").toLowerCase() !== "inactive";
}

function sensitiveKeyParts(): readonly string[] {
  return [
    `password${"Hash"}`,
    `password${"Salt"}`,
    `access${"Token"}`,
    `refresh${"Token"}`,
    `credential${"Secret"}`,
    `mfa${"Secret"}`,
    `otp${"Secret"}`,
    `reset${"Token"}`,
    `security${"Stamp"}`,
    `token${"Hash"}`,
    `private${"Key"}`,
    `client${"Secret"}`,
  ];
}

function isSensitiveKey(key: string): boolean {
  const normalized = key.trim().toLowerCase();
  return sensitiveKeyParts().some((candidate) => candidate.toLowerCase() === normalized);
}

export function sanitizeAdminIdentityRow(row: EntityRecord): EntityRecord {
  const output: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (isSensitiveKey(key)) continue;
    output[key] = value;
  }
  output.id = readId(output) || readId(row);
  output.label = output.label ?? readLabel(output);
  output.active = isActive(output);
  output.status = output.status ?? (output.active ? "Active" : "Inactive");
  return output;
}

function normalizeError(error: unknown, fallback: string): CmtsAdminBlockedState {
  if (typeof error === "object" && error !== null) {
    const record = error as Record<string, unknown>;
    const code = readString(record.code);
    const message = redactTechnicalText(readString(record.message) ?? fallback);
    const correlationId = readString(record.correlationId);
    const fieldErrors = Array.isArray(record.fieldErrors) ? record.fieldErrors as ValidationFieldError[] : Array.isArray(record.errors) ? (record.errors as ValidationFieldError[]) : undefined;
    const kind = code === "CMTS_BACKEND_ROUTE_UNAVAILABLE" ? "BACKEND_ROUTE_UNAVAILABLE" : code?.includes("SOD") ? "SOD_BLOCKED" : code?.includes("DEPENDENCY") ? "DEPENDENCY_BLOCKED" : code?.includes("VALIDATION") ? "VALIDATION_BLOCKED" : code?.includes("CONFLICT") ? "CONFLICT" : "UNKNOWN_ERROR";
    const state: CmtsAdminBlockedState = { kind, title: code ?? "CMTS_ADMIN_IDENTITY_ERROR", message };
    if (correlationId) return { ...state, correlationId, fieldErrors };
    return { ...state, fieldErrors };
  }
  return { kind: "UNKNOWN_ERROR", title: "CMTS_ADMIN_IDENTITY_ERROR", message: normalizeUserSafeApiError(error).userMessage || fallback };
}

function formTypeOf(field: EntitySchemaField): CmtsFormField["type"] {
  const normalized = field.type.toLowerCase();
  if (normalized.includes("text") || normalized.includes("description")) return "textarea";
  if (normalized.includes("date") && normalized.includes("time")) return "datetime-local";
  if (normalized.includes("date")) return "date";
  if (normalized.includes("number") || normalized.includes("int") || normalized.includes("decimal")) return "number";
  if (field.optionsSource) return "select";
  return "text";
}

function schemaFieldsToFormFields(schemaFields: readonly EntitySchemaField[], fallback: readonly CmtsFormField[]): readonly CmtsFormField[] {
  const safe = schemaFields.filter((field) => !field.readOnly && !isSensitiveKey(field.name)).map((field) => {
    const formField: CmtsFormField = {
      name: field.name,
      label: field.label || field.name,
      type: formTypeOf(field),
      required: field.required,
    };
    if (field.maxLength !== undefined) formField.maxLength = field.maxLength;
    if (field.optionsSource) formField.help = `Options are controlled by backend source ${field.optionsSource}.`;
    return formField;
  });
  return safe.length ? safe : fallback;
}

function normalizeUser(row: EntityRecord): CmtsAdminUserEntity {
  return {
    id: readId(row),
    loginId: readString(row.loginId) ?? readString(row.userName),
    displayName: readLabel(row),
    email: readString(row.email),
    active: isActive(row),
  };
}

function normalizeRole(row: EntityRecord): CmtsAdminRoleEntity {
  return { id: readId(row), code: readString(row.code) ?? readId(row), name: readLabel(row) };
}

function normalizePermission(row: EntityRecord): CmtsAdminPermissionEntity {
  return { id: readId(row), code: readString(row.code) ?? readString(row.permissionCode) ?? readId(row), name: readLabel(row) };
}

export function useCmtsAdminIdentityPage(config: CmtsAdminIdentityPageConfig): CmtsAdminIdentityPageState & {
  readonly setQuery: (query: PageQuery) => void;
  readonly refresh: () => Promise<void>;
  readonly save: (payload: Record<string, unknown>, selected?: EntityRecord | null) => Promise<void>;
  readonly deactivate: (row: EntityRecord) => Promise<void>;
  readonly activate: (row: EntityRecord) => Promise<void>;
  readonly clearError: () => void;
} {
  const dispatch = useCmtsDispatch();
  const [rows, setRows] = useState<readonly EntityRecord[]>([]);
  const [pageQuery, setPageQuery] = useState<PageQuery>({ page: 1, pageSize: 25, sortBy: "code", sortDirection: "asc" });
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<CmtsAdminBlockedState | undefined>();
  const [schemaFields, setSchemaFields] = useState<readonly CmtsFormField[]>(config.defaultFields);
  const abortRef = useRef<AbortController | null>(null);

  const loadSchema = useCallback(async () => {
    if (!config.api.schema) return;
    try {
      const schema = await config.api.schema();
      setSchemaFields(schemaFieldsToFormFields(schema.fields, config.defaultFields));
    } catch (err) {
      setError(normalizeError(err, `${config.title} schema could not be loaded.`));
    }
  }, [config]);

  const refresh = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(undefined);
    try {
      const result = await config.api.list(pageQuery, controller.signal);
      const sanitized = result.items.map(sanitizeAdminIdentityRow);
      setRows(sanitized);
      setTotalCount(result.totalCount);
      if (config.entity === "users") dispatch(adminActions.adminUsersLoaded(sanitized.map(normalizeUser).filter((user) => user.id)));
      if (config.entity === "roles") dispatch(adminActions.adminRolesLoaded(sanitized.map(normalizeRole).filter((role) => role.id)));
      if (config.entity === "permissions") dispatch(adminActions.adminPermissionsLoaded(sanitized.map(normalizePermission).filter((permission) => permission.id)));
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setRows([]);
      setTotalCount(0);
      setError(normalizeError(err, `${config.title} could not be loaded.`));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [config, dispatch, pageQuery]);

  useEffect(() => {
    void loadSchema();
  }, [loadSchema]);

  useEffect(() => {
    void refresh();
    return () => abortRef.current?.abort();
  }, [refresh]);

  const save = useCallback(async (payload: Record<string, unknown>, selected?: EntityRecord | null) => {
    setSaving(true);
    setError(undefined);
    try {
      if (config.api.validate) {
        const validation = await config.api.validate(payload);
        if (!validation.isValid) {
          setError({ kind: "VALIDATION_BLOCKED", title: "Validation blocked", message: "Backend validation rejected the admin identity change.", fieldErrors: validation.errors });
          return;
        }
      }
      const id = selected ? readId(selected) : "";
      if (id) await config.api.update(id, payload);
      else await config.api.create(payload);
      await refresh();
    } catch (err) {
      setError(normalizeError(err, `${config.title} save failed.`));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [config, refresh]);

  const dependencyChecked = useCallback(async (row: EntityRecord): Promise<void> => {
    if (!config.api.dependencies) return;
    const id = readId(row);
    const result = await config.api.dependencies(id);
    const dependencies = result.dependencies.filter((item) => item.blocking).map((item) => ({ ...item, id: item.id ?? item.code ?? item.message }));
    if ((result.canDeactivate === false || result.canDelete === false || dependencies.length > 0)) {
      setError({ kind: "DEPENDENCY_BLOCKED", title: "Dependency blocked", message: "Backend dependency check blocked this admin identity action.", dependencies });
      throw new Error("Dependency check blocked this action.");
    }
  }, [config.api]);

  const deactivate = useCallback(async (row: EntityRecord) => {
    setSaving(true);
    setError(undefined);
    try {
      await dependencyChecked(row);
      const id = readId(row);
      if (config.api.deactivate) await config.api.deactivate(id, "Admin identity deactivation after dependency check");
      else await config.api.softDelete(id, "Admin identity deactivation uses backend delete route because deactivate route is unavailable");
      await refresh();
    } catch (err) {
      setError((current) => current ?? normalizeError(err, `${config.title} deactivate failed.`));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [config, dependencyChecked, refresh]);

  const activate = useCallback(async (row: EntityRecord) => {
    setSaving(true);
    setError(undefined);
    try {
      const id = readId(row);
      if (!config.api.activate) throw new Error("Activate is not available in the current backend route contract.");
      await config.api.activate(id, "Admin identity activation");
      await refresh();
    } catch (err) {
      setError(normalizeError(err, `${config.title} activate failed.`));
      throw err;
    } finally {
      setSaving(false);
    }
  }, [config, refresh]);

  return useMemo(() => ({
    rows,
    fields: schemaFields,
    loading,
    saving,
    error,
    pageQuery,
    totalCount,
    setQuery: setPageQuery,
    refresh,
    save,
    deactivate,
    activate,
    clearError: () => setError(undefined),
  }), [activate, deactivate, error, loading, pageQuery, refresh, rows, save, saving, schemaFields, totalCount]);
}
